import { forwardRef, type HTMLAttributes, type CSSProperties } from 'react';
import { clsx } from 'clsx';

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  width?: string | number;
  height?: string | number;
  rounded?: 'sm' | 'md' | 'full';
}

const roundedMap: Record<NonNullable<SkeletonProps['rounded']>, string> = {
  sm: 'rounded-sm',
  md: 'rounded-md',
  full: 'rounded-full',
};

function toCss(value: string | number | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  return typeof value === 'number' ? `${value}px` : value;
}

export const Skeleton = forwardRef<HTMLDivElement, SkeletonProps>(function Skeleton(
  { width, height, rounded = 'md', style, className, ...rest },
  ref
) {
  const inlineStyle: CSSProperties = {
    ...style,
    width: toCss(width) ?? style?.width,
    height: toCss(height) ?? style?.height,
  };
  return (
    <div
      ref={ref}
      style={inlineStyle}
      className={clsx('bg-subtle animate-pulse', roundedMap[rounded], className)}
      {...rest}
    />
  );
});

interface SkeletonTextProps extends HTMLAttributes<HTMLDivElement> {
  lines?: number;
}

export const SkeletonText = forwardRef<HTMLDivElement, SkeletonTextProps>(function SkeletonText(
  { lines = 1, className, ...rest },
  ref
) {
  return (
    <div ref={ref} className={clsx('flex flex-col gap-2', className)} {...rest}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={12} width={i === lines - 1 && lines > 1 ? '70%' : '100%'} />
      ))}
    </div>
  );
});
